import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Heading,
  Button,
  useDisclosure,
  Spinner,
  Field,
  Input,
  Textarea,
  Dialog,
  VStack,
  HStack,
  Stack,
  IconButton,
  Badge,
  Text,
  Avatar,
  NativeSelect,
  Checkbox,
  Menu,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, organizationsAPI } from '../services/api';
import { Project, Organization } from '../types';
import KanbanBoard from '../components/KanbanBoard';
import TaskListView from '../components/TaskListView';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/toast';
import { getStoredProjectViewMode } from '../utils/projectViewPreferences';

const ProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const { open, onOpen } = useDisclosure();
  const { open: archiveDialogOpen, onOpen: onArchiveDialogOpen, onClose: onArchiveDialogClose } = useDisclosure();
  const { open: deleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const { open: detailsDialogOpen, onOpen: onDetailsDialogOpen, onClose: onDetailsDialogClose } = useDisclosure();
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');
  const [projectOrganizationId, setProjectOrganizationId] = useState('');
  const [projectMembers, setProjectMembers] = useState<string[]>([]);
  const [createOrgDetails, setCreateOrgDetails] = useState<Organization | null>(null);
  const [createOrgMembers, setCreateOrgMembers] = useState<Array<{ _id: string; name: string; email: string }>>([]);
  const [createMembersSearch, setCreateMembersSearch] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgDetails, setOrgDetails] = useState<Organization | null>(null);
  const [orgMembersList, setOrgMembersList] = useState<Array<{ _id: string; name: string; email: string }>>([]);
  const [memberToAdd, setMemberToAdd] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editReason, setEditReason] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsMembersSearch, setDetailsMembersSearch] = useState('');
  const [editMembers, setEditMembers] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projectViewMode, setProjectViewMode] = useState(() => getStoredProjectViewMode(user?.id));
  const creatorId = (project?.createdBy as any)?._id || (project?.createdBy as any)?.id;
  const isAdmin = user?.role === 'admin';
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const canManageProject = !!(isAdmin || isMasterAdmin || (creatorId && user?.id && creatorId.toString() === user.id));

  useEffect(() => {
    if (user?.id) {
      setProjectViewMode(getStoredProjectViewMode(user.id));
    }
  }, [user?.id]);

  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject();
    } else {
      setLoading(false);
      onOpen();
      loadOrganizationsForCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadOrganizationsForCreate = async () => {
    try {
      const res = await organizationsAPI.getAll();
      setOrganizations(res.data || []);
      if (res.data?.length) {
        setProjectOrganizationId(res.data[0]._id);
      }
    } catch {
      setOrganizations([]);
    }
  };

  useEffect(() => {
    if (projectId === 'new' && projectOrganizationId) {
      setProjectMembers([]);
      setCreateMembersSearch('');
      Promise.all([
        organizationsAPI.getById(projectOrganizationId),
        organizationsAPI.getMembers(projectOrganizationId),
      ]).then(([orgRes, membersRes]) => {
        setCreateOrgDetails(orgRes.data);
        setCreateOrgMembers(membersRes.data || []);
      }).catch(() => {
        setCreateOrgDetails(null);
        setCreateOrgMembers([]);
      });
    } else {
      setCreateOrgDetails(null);
      setCreateOrgMembers([]);
    }
  }, [projectId, projectOrganizationId]);

  const loadProject = async () => {
    if (!projectId) return;
    try {
      const response = await projectsAPI.getById(projectId);
      setProject(response.data);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to load project',
        status: 'error',
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgDetails = useCallback(async () => {
    const orgId = typeof project?.organization === 'string' ? project.organization : (project?.organization as any)?._id;
    if (!orgId) return;
    try {
      const [orgRes, membersRes] = await Promise.all([
        organizationsAPI.getById(orgId),
        organizationsAPI.getMembers(orgId),
      ]);
      setOrgDetails(orgRes.data);
      setOrgMembersList(membersRes.data || []);
    } catch {
      setOrgDetails(null);
      setOrgMembersList([]);
    }
  }, [project?.organization]);

  useEffect(() => {
    if (detailsDialogOpen && project) {
      loadOrgDetails();
      setEditName(project.name || '');
      setEditDescription(project.description || '');
      setEditStartDate(project.startDate ? new Date(project.startDate as string).toISOString().split('T')[0] : '');
      setEditEndDate(project.endDate ? new Date(project.endDate as string).toISOString().split('T')[0] : '');
      setEditReason('');
      setDetailsMembersSearch('');
      setEditMembers(
        (project.members || [])
          .map((m: any) => (m._id || m.id)?.toString())
          .filter(Boolean)
      );
    }
  }, [detailsDialogOpen, project, loadOrgDetails]);

  const handleAddProjectMember = async () => {
    if (!projectId || !memberToAdd) return;
    setMembersLoading(true);
    try {
      await projectsAPI.addMember(projectId, memberToAdd);
      toast({ title: 'Success', description: 'Member added to project', status: 'success' });
      setMemberToAdd('');
      loadProject();
      loadOrgDetails();
    } catch (e: any) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not add member', status: 'error' });
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRemoveProjectMember = async (userId: string) => {
    if (!projectId) return;
    try {
      await projectsAPI.removeMember(projectId, userId);
      toast({ title: 'Success', description: 'Member removed from project', status: 'success' });
      loadProject();
    } catch (e: any) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not remove', status: 'error' });
    }
  };

  const handleArchiveClick = () => {
    onArchiveDialogOpen();
  };

  const handleArchiveProject = async () => {
    if (!project || !projectId) return;
    
    const isArchived = project.status === 'archived';

    try {
      await projectsAPI.archive(projectId, !isArchived);
      toast({
        title: 'Success',
        description: `Project ${isArchived ? 'restored to active' : 'archived'}`,
        status: 'success',
      });
      onArchiveDialogClose();
      await loadProject();
      // Navigate to dashboard after archiving
      if (!isArchived) {
        setTimeout(() => navigate('/dashboard'), 1000);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || `Failed to ${isArchived ? 'restore' : 'archive'} project`,
        status: 'error',
      });
    }
  };

  const handleDeleteClick = () => {
    onDeleteDialogOpen();
  };

  const handleDeleteProject = async () => {
    if (!project || !projectId) return;

    try {
      await projectsAPI.delete(projectId);
      toast({
        title: 'Success',
        description: 'Project permanently deleted',
        status: 'success',
      });
      onDeleteDialogClose();
      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete project',
        status: 'error',
      });
    }
  };

  const toggleEditMember = (userId: string) => {
    setEditMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSaveProjectDetails = async () => {
    if (!project || !projectId) return;

    if (!editName.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        status: 'error',
      });
      return;
    }

    const originalStart = project.startDate ? new Date(project.startDate as string).toISOString().split('T')[0] : '';
    const originalEnd = project.endDate ? new Date(project.endDate as string).toISOString().split('T')[0] : '';
    const startChanged = originalStart !== editStartDate;
    const endChanged = originalEnd !== editEndDate;

    if ((startChanged || endChanged) && !editReason.trim()) {
      toast({
        title: 'Error',
        description: 'You must provide a reason for the project date change',
        status: 'error',
      });
      return;
    }

    setSavingDetails(true);
    try {
      // prvo osnovna polja i članovi (bez datuma)
      const originalMemberIds = (project.members || [])
        .map((m: any) => (m._id || m.id)?.toString())
        .filter(Boolean);
      const membersChanged =
        originalMemberIds.length !== editMembers.length ||
        originalMemberIds.some((id) => !editMembers.includes(id));

      if (
        editName !== project.name ||
        editDescription !== (project.description || '') ||
        membersChanged
      ) {
        await projectsAPI.update(projectId, {
          name: editName,
          description: editDescription,
          members: editMembers,
        });
      }

      // zatim datumi sa razlogom (ako su promenjeni)
      if (startChanged || endChanged) {
        await projectsAPI.updateDates(projectId, {
          startDate: editStartDate || null,
          endDate: editEndDate || null,
          reason: editReason.trim(),
        });
      }

      toast({
        title: 'Success',
        description: 'Project details saved',
        status: 'success',
      });

      await loadProject();
      onDetailsDialogClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Could not save project details',
        status: 'error',
      });
    } finally {
      setSavingDetails(false);
    }
  };

  const toggleCreateMember = (userId: string) => {
    setProjectMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        status: 'error',
      });
      return;
    }
    if (!projectOrganizationId) {
      toast({
        title: 'Error',
        description: 'Select an organization',
        status: 'error',
      });
      return;
    }

    try {
      const response = await projectsAPI.create({
        name: projectName,
        description: projectDescription,
        organization: projectOrganizationId,
        members: projectMembers,
        startDate: projectStartDate || undefined,
        endDate: projectEndDate || undefined,
      });

      navigate(`/project/${response.data._id}`);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create project',
        status: 'error',
      });
    }
  };

  const formatHistoryDate = (dateString?: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Spinner size="xl" color="purple.500" />
      </Box>
    );
  }

  if (projectId === 'new') {
    return (
      <Container maxW="container.xl" py={8}>
        <Dialog.Root open={open} onOpenChange={(e: { open: boolean }) => !e.open && navigate('/dashboard')}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Create New Project</Dialog.Title>
                <Dialog.CloseTrigger />
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4}>
                  <Field.Root required>
                    <Field.Label>Project name</Field.Label>
                    <Input
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="My project"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Textarea
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="Project description"
                      rows={3}
                    />
                  </Field.Root>
                  <Field.Root required>
                    <Field.Label>Organization</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={projectOrganizationId}
                        onChange={(e: any) => setProjectOrganizationId(e.target.value)}
                      >
                        {organizations.map((org) => (
                          <option key={org._id} value={org._id}>{org.name}</option>
                        ))}
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Field.Root>
                  <HStack gap={4} width="100%">
                    <Field.Root flex={1}>
                      <Field.Label>Start date</Field.Label>
                      <Input
                        type="date"
                        value={projectStartDate}
                        onChange={(e) => setProjectStartDate(e.target.value)}
                      />
                    </Field.Root>
                    <Field.Root flex={1}>
                      <Field.Label>End date</Field.Label>
                      <Input
                        type="date"
                        value={projectEndDate}
                        onChange={(e) => setProjectEndDate(e.target.value)}
                      />
                    </Field.Root>
                  </HStack>
                  {createOrgMembers.length > 0 && (
                    <Field.Root width="100%">
                      <Field.Label>Project members (organization members only)</Field.Label>
                      <Text fontSize="xs" color="gray.500" mb={2}>Select who will be on the project</Text>
                      <Input
                        placeholder="Search by name or email..."
                        value={createMembersSearch}
                        onChange={(e) => setCreateMembersSearch(e.target.value)}
                        mb={2}
                        size="sm"
                      />
                      <VStack gap={2} align="stretch" maxH="280px" overflowY="auto" width="100%">
                        {createOrgMembers
                          .filter((u) => {
                            const q = createMembersSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (u.name?.toLowerCase() || '').includes(q) || (u.email?.toLowerCase() || '').includes(q);
                          })
                          .map((u) => {
                            const id = u._id;
                            const isChecked = projectMembers.includes(id);
                            return (
                              <HStack
                                key={id}
                                p={2}
                                borderRadius="md"
                                bg={isChecked ? 'purple.50' : 'gray.50'}
                                borderWidth={1}
                                borderColor={isChecked ? 'purple.200' : 'gray.200'}
                                cursor="pointer"
                                onClick={() => toggleCreateMember(id)}
                              >
                                <Checkbox.Root
                                  checked={isChecked}
                                  onCheckedChange={() => toggleCreateMember(id)}
                                  cursor="pointer"
                                >
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control cursor="pointer" />
                                  <Checkbox.Label ml={2} flex={1}>
                                    <HStack>
                                      <Avatar.Root size="xs">
                                        <Avatar.Fallback bg="gray.200" color="black">
                                          {u?.name?.charAt(0) || '?'}
                                        </Avatar.Fallback>
                                      </Avatar.Root>
                                      <Text fontSize="sm">
                                        {u?.name} ({u?.email})
                                      </Text>
                                    </HStack>
                                  </Checkbox.Label>
                                </Checkbox.Root>
                              </HStack>
                            );
                          })}
                      </VStack>
                    </Field.Root>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack>
                  <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                    Cancel
                  </Button>
                  <Button colorScheme="blue" onClick={handleCreateProject}>
                    Create project
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Box bg="white" borderBottomWidth={1} borderColor="gray.200" boxShadow="sm" mb={6}>
        <Container maxW="container.xl" py={4}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ base: 'flex-start', md: 'center' }}
            gap={{ base: 3, md: 4 }}
          >
            <HStack gap={4} flexWrap="wrap" alignItems="center">
              <IconButton
                aria-label="Back"
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                borderRadius="full"
                _hover={{ bg: 'gray.100' }}
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </IconButton>
              <Heading size="xl" color="gray.800">{project?.name || 'Project'}</Heading>
              {project && (
                <>
                  <Badge 
                    colorScheme={project.status === 'archived' ? 'gray' : 'green'}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    {project.status === 'archived' ? 'Archived' : 'Active'}
                  </Badge>
                  {(project.startDate || project.endDate) && (
                    <Text fontSize="sm" color="gray.600">
                      {project.startDate && new Date(project.startDate as string).toLocaleDateString('en-GB')}
                      {project.startDate && project.endDate && ' – '}
                      {project.endDate && new Date(project.endDate as string).toLocaleDateString('en-GB')}
                    </Text>
                  )}
                </>
              )}
              {project && (
                <Menu.Root positioning={{ placement: 'bottom-end', gutter: 8 }}>
                  <Menu.Trigger asChild>
                    <IconButton
                      aria-label="Project actions"
                      variant="ghost"
                      borderRadius="full"
                      size="sm"
                      display={{ base: 'inline-flex', md: 'none' }}
                    >
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"
                        />
                      </svg>
                    </IconButton>
                  </Menu.Trigger>
                  <Menu.Content
                    bg="white"
                    borderRadius={{ base: 0, md: 'xl' }}
                    boxShadow="xl"
                    py={2}
                    px={1}
                    minW={{ base: '100%', md: '180px' }}
                    position={{ base: 'fixed', md: 'relative' }}
                    top={{ base: '64px', md: 'auto' }}
                    left={{ base: 0, md: 'auto' }}
                    right={{ base: 0, md: 'auto' }}
                    zIndex={25}
                  >
                    {canManageProject && (
                      <Menu.Item
                        onClick={onDetailsDialogOpen}
                        _hover={{ bg: 'gray.50' }}
                        py={2}
                        px={3}
                        borderRadius="lg"
                      >
                        Project details
                      </Menu.Item>
                    )}
                    {(isAdmin || isMasterAdmin) && (
                      <>
                        <Menu.Item
                          onClick={handleArchiveClick}
                          _hover={{ bg: 'gray.50' }}
                          py={2}
                          px={3}
                          borderRadius="lg"
                        >
                          {project.status === 'archived' ? 'Unarchive project' : 'Archive project'}
                        </Menu.Item>
                        {project.status === 'archived' && (
                          <Menu.Item
                            onClick={handleDeleteClick}
                            color="red.500"
                            _hover={{ bg: 'red.50' }}
                            py={2}
                            px={3}
                            borderRadius="lg"
                          >
                            Delete permanently
                          </Menu.Item>
                        )}
                      </>
                    )}
                  </Menu.Content>
                </Menu.Root>
              )}
            </HStack>
            <Stack
              direction={{ base: 'column', sm: 'row' }}
              gap={2}
              justifyContent={{ base: 'flex-start', md: 'flex-end' }}
              alignItems={{ base: 'stretch', sm: 'center' }}
              display={{ base: 'none', md: 'flex' }}
            >
              {project && canManageProject && (
                <Button
                  variant="ghost"
                  size="sm"
                  borderRadius="full"
                  onClick={onDetailsDialogOpen}
                >
                  Project details
                </Button>
              )}
              {project && (isAdmin || isMasterAdmin) && (
                <>
                  {project.status === 'archived' ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        colorScheme="green"
                        borderRadius="full"
                        onClick={handleArchiveClick}
                        _hover={{
                          bg: 'green.50',
                        }}
                      >
                        Unarchive
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        colorScheme="red"
                        borderRadius="full"
                        onClick={handleDeleteClick}
                        _hover={{
                          bg: 'red.50',
                        }}
                      >
                        Delete permanently
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      colorScheme="gray"
                      borderRadius="full"
                      onClick={handleArchiveClick}
                      _hover={{
                        bg: 'gray.100',
                      }}
                    >
                      Archive
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box w="100%" px={{ base: 4, md: 6, lg: 8 }} py={8}>
        {project && (
          projectViewMode === 'list' ? (
            <TaskListView projectId={project._id} project={project} onProjectUpdate={loadProject} />
          ) : (
            <KanbanBoard
              projectId={project._id}
              project={project}
              onProjectUpdate={loadProject}
            />
          )
        )}
      </Box>

      {/* Archive Confirmation Dialog (only admins / master admins) */}
      {project && (isAdmin || isMasterAdmin) && (
        <Dialog.Root open={archiveDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onArchiveDialogClose()}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>
                  {project.status === 'archived' ? 'Restore project' : 'Archive project'}
                </Dialog.Title>
                <Dialog.CloseTrigger />
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Are you sure you want to {project.status === 'archived' ? 'restore' : 'archive'} the project <strong>{project.name}</strong>?
                  {project.status !== 'archived' && ' Archived projects will not appear in the main project list.'}
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack>
                  <Button variant="ghost" onClick={onArchiveDialogClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme={project.status === 'archived' ? 'green' : 'gray'}
                    onClick={handleArchiveProject}
                  >
                    {project.status === 'archived' ? 'Restore' : 'Archive'}
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}

      {/* Delete Confirmation Dialog (only admins / master admins) */}
      {project && project.status === 'archived' && (isAdmin || isMasterAdmin) && (
        <Dialog.Root open={deleteDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onDeleteDialogClose()}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Permanently delete project</Dialog.Title>
                <Dialog.CloseTrigger />
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={2} align="stretch">
                  <Text>
                    Are you sure you want to permanently delete the project <strong>{project.name}</strong>?
                  </Text>
                  <Text fontSize="sm" color="red.600" fontWeight="semibold">
                    ⚠️ Warning: This action is permanent and cannot be undone. All tasks and project data will be deleted.
                  </Text>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack>
                  <Button variant="ghost" onClick={onDeleteDialogClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={handleDeleteProject}
                  >
                    Delete permanently
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}

      {/* Project details dialog */}
      {project && (
        <Dialog.Root
          open={detailsDialogOpen}
          onOpenChange={(e: { open: boolean }) => !e.open && onDetailsDialogClose()}
          size="full"
        >
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              maxW="100vw"
              maxH="100vh"
              w="100vw"
              h="100vh"
              borderRadius={0}
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <Dialog.Header flexShrink={0}>
                <Dialog.Title>Project details</Dialog.Title>
                <Dialog.CloseTrigger />
              </Dialog.Header>
              <Dialog.Body flex="1" overflowY="auto">
                <VStack gap={4} align="stretch" px={{ base: 4, md: 6 }} py={4}>
                  <Field.Root>
                    <Field.Label>Project name</Field.Label>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Project name"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Project description"
                      rows={3}
                    />
                  </Field.Root>
                  <HStack gap={4} width="100%">
                    <Field.Root flex={1}>
                      <Field.Label>Start date</Field.Label>
                      <Input
                        type="date"
                        value={editStartDate}
                        onChange={(e) => setEditStartDate(e.target.value)}
                      />
                    </Field.Root>
                    <Field.Root flex={1}>
                      <Field.Label>End date</Field.Label>
                      <Input
                        type="date"
                        value={editEndDate}
                        onChange={(e) => setEditEndDate(e.target.value)}
                      />
                    </Field.Root>
                  </HStack>
                  <Field.Root>
                    <Field.Label>Reason for date change</Field.Label>
                    <Textarea
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      placeholder="Required when changing project start or end date"
                      rows={3}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      If you are not changing dates, this field can stay empty. If you are moving the project start or end date, a reason is required.
                    </Text>
                  </Field.Root>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Kreator</Text>
                    <HStack>
                      <Avatar.Root size="sm">
                        <Avatar.Fallback bg="gray.200" color="black">
                          {(project.createdBy as any)?.name?.charAt(0) || 'K'}
                        </Avatar.Fallback>
                      </Avatar.Root>
                      <Text fontSize="sm">
                        {(project.createdBy as any)?.name} ({(project.createdBy as any)?.email})
                      </Text>
                      <Badge colorScheme="purple" size="sm">Kreator</Badge>
                    </HStack>
                  </Box>
                  {project.dateChangeHistory && project.dateChangeHistory.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                        Date change history
                      </Text>
                      <VStack gap={1} align="stretch" maxH="180px" overflowY="auto">
                        {[...project.dateChangeHistory]
                          .slice()
                          .sort((a: any, b: any) => {
                            const ad = a.changedAt || a.newValue;
                            const bd = b.changedAt || b.newValue;
                            return new Date(bd).getTime() - new Date(ad).getTime();
                          })
                          .map((h: any, idx: number) => {
                            const label =
                              h.field === 'startDate' ? 'Start' : 'End';
                            const oldLabel = h.oldValue
                              ? new Date(h.oldValue).toLocaleDateString('en-GB')
                              : 'n/a';
                            const newLabel = h.newValue
                              ? new Date(h.newValue).toLocaleDateString('en-GB')
                              : 'n/a';
                            return (
                              <HStack
                                key={idx}
                                justifyContent="space-between"
                                alignItems="center"
                                p={2}
                                borderWidth={1}
                                borderRadius="md"
                                bg="gray.50"
                                borderColor="gray.200"
                                gap={3}
                              >
                                <Text fontSize="xs" color="gray.700">
                                  <strong>{label}:</strong> {oldLabel} → {newLabel}{' '}
                                  <Text as="span" fontWeight="normal" color="gray.500">
                                    ({h.reason})
                                  </Text>
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  textAlign="right"
                                  minW="120px"
                                >
                                  {h.changedBy?.name && (
                                    <>
                                      {h.changedBy.name},{' '}
                                    </>
                                  )}
                                  {h.changedAt && formatHistoryDate(h.changedAt)}
                                </Text>
                              </HStack>
                            );
                          })}
                      </VStack>
                    </Box>
                  )}
                  {orgMembersList.length > 0 && (
                    <Field.Root width="100%">
                      <Field.Label>Project members (organization members only)</Field.Label>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        Select who is on the project. Changes will be saved when you click "Save".
                      </Text>
                      <Input
                        placeholder="Search by name or email..."
                        value={detailsMembersSearch}
                        onChange={(e) => setDetailsMembersSearch(e.target.value)}
                        mb={2}
                        size="sm"
                      />
                      <VStack gap={2} align="stretch" maxH="280px" overflowY="auto" width="100%">
                        {orgMembersList
                          .filter((u) => {
                            const q = detailsMembersSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (
                              (u.name?.toLowerCase() || '').includes(q) ||
                              (u.email?.toLowerCase() || '').includes(q)
                            );
                          })
                          .map((u) => {
                            const id = u._id;
                            const isChecked = editMembers.includes(id);
                            return (
                              <HStack
                                key={id}
                                p={2}
                                borderRadius="md"
                                bg={isChecked ? 'purple.50' : 'gray.50'}
                                borderWidth={1}
                                borderColor={isChecked ? 'purple.200' : 'gray.200'}
                                cursor="pointer"
                                onClick={() => toggleEditMember(id)}
                              >
                                <Checkbox.Root
                                  checked={isChecked}
                                  onCheckedChange={() => toggleEditMember(id)}
                                  cursor="pointer"
                                >
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control cursor="pointer" />
                                  <Checkbox.Label ml={2} flex={1}>
                                    <HStack>
                                      <Avatar.Root size="xs">
                                        <Avatar.Fallback bg="gray.200" color="black">
                                          {u?.name?.charAt(0) || '?'}
                                        </Avatar.Fallback>
                                      </Avatar.Root>
                                      <Text fontSize="sm">
                                        {u?.name} ({u?.email})
                                      </Text>
                                    </HStack>
                                  </Checkbox.Label>
                                </Checkbox.Root>
                              </HStack>
                            );
                          })}
                      </VStack>
                    </Field.Root>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack>
                  <Button variant="ghost" onClick={onDetailsDialogClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleSaveProjectDetails}
                    loading={savingDetails}
                  >
                    Save
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}
    </Box>
  );
};

export default ProjectPage;
